import React from 'react'
import { ButtonProps as BadgerButtonProps, ButtonStyles } from '@grammarly/badger-ui'

export enum ButtonStatus {
  Active = 'Active',
  Disabled = 'Disabled',
  Loading = 'Loading',
}

const statusMap = {
  [ButtonStatus.Active]: 'enabled',
  [ButtonStatus.Disabled]: 'disabled',
  [ButtonStatus.Loading]: 'loading',
} as const

export const buttonStatusToBadgerStatus = (status?: ButtonStatus): BadgerButtonProps['status'] => {
  if (status) {
    return statusMap[status]
  }
  return undefined
}

export enum ButtonColor {
  // WDK 2024 Brand Refresh
  WDK24PrimaryLightBg = '24PrimaryLightBg',
  WDK24SecondaryLightBg = '24SecondaryLightBg',
  WDK24TertiaryLightBg = '24TertiaryLightBg',
  WDK24PrimaryDarkBg = '24PrimaryDarkBg',
  WDK24SecondaryDarkBg = '24SecondaryDarkBg',
  WDK24TertiaryDarkBg = '24TertiaryDarkBg',
  WDK24LimeGreenDarkBg = '24LimeGreenDarkBg',
  WDK24LimeGreenBlackTextDarkBg = '24LimeGreenBlackTextDarkBg',
  WDK24GoogleSecondaryDarkBg = '24GoogleSecondaryDarkBg',
  WDK24BlackWhiteText = '24BlackWhiteText',
  WDK24WhiteBlackText = '24WhiteBlackText',

  PrimaryGreen = 'PrimaryGreen',
  PrimaryOnDarkBg = 'PrimaryOnDarkBg',
  Secondary = 'Secondary',
  SecondaryOnDarkBg = 'SecondaryOnDarkBg',
  Tertiary = 'Tertiary',
  GoPrimaryOnDarkBg = 'GoPrimaryOnDarkBg',
  GoSecondaryOnDarkBg = 'GoSecondaryOnDarkBg',

  // The following button colors will be deprecated:
  Transparent = 'Transparent',
  PrimaryBlue = 'PrimaryBlue',
  SecondaryBlue = 'SecondaryBlue',
  SecondaryGreen = 'SecondaryGreen',
}

const buttonColorMap = {
  [ButtonColor.WDK24PrimaryLightBg]: ButtonStyles.GreenButtonWhiteText24,
  [ButtonColor.WDK24SecondaryLightBg]: ButtonStyles.TransparentButtonGreenTextWithBorder24,
  [ButtonColor.WDK24TertiaryLightBg]: ButtonStyles.TransparentButtonGreenText24,
  [ButtonColor.WDK24PrimaryDarkBg]: ButtonStyles.WhiteButtonBlackText24,
  [ButtonColor.WDK24SecondaryDarkBg]: ButtonStyles.TransparentButtonWhiteTextWithBorder24,
  [ButtonColor.WDK24TertiaryDarkBg]: ButtonStyles.TransparentButtonWhiteText24,
  [ButtonColor.WDK24LimeGreenDarkBg]: ButtonStyles.TransparentButtonLimeGreenTextWithBorder24,
  [ButtonColor.WDK24LimeGreenBlackTextDarkBg]: ButtonStyles.LimeGreenButtonBlackText24,
  [ButtonColor.WDK24GoogleSecondaryDarkBg]: ButtonStyles.GoogleBlackButtonWhiteText24,
  [ButtonColor.WDK24BlackWhiteText]: ButtonStyles.BlackButtonWhiteText24,
  [ButtonColor.WDK24WhiteBlackText]: ButtonStyles.WhiteButtonBlackTextBlackOutline24,

  [ButtonColor.PrimaryGreen]: ButtonStyles.GreenButtonWhiteText,
  [ButtonColor.PrimaryOnDarkBg]: ButtonStyles.WhiteButtonBlueText,
  [ButtonColor.Secondary]: ButtonStyles.WhiteButtonGreenTextWithBorder,
  [ButtonColor.SecondaryOnDarkBg]: ButtonStyles.TransparentButtonWhiteTextWithBorder,
  [ButtonColor.Tertiary]: ButtonStyles.TransparentButtonGrayText,
  [ButtonColor.GoPrimaryOnDarkBg]: ButtonStyles.GoWhiteButtonDarkGreenText,
  [ButtonColor.GoSecondaryOnDarkBg]: ButtonStyles.GoTransparentButtonWhiteTextWithBorder,

  // The following buttons will be deprecated
  [ButtonColor.Transparent]: ButtonStyles.TransparentButtonGrayText,
  [ButtonColor.PrimaryBlue]: ButtonStyles.BlueButtonWhiteText,
  [ButtonColor.SecondaryGreen]: ButtonStyles.GreyButtonGreenText,
  [ButtonColor.SecondaryBlue]: ButtonStyles.GreyButtonBlueText,
}

const buttonColorTo2024: Record<ButtonColor, ButtonColor> = {
  [ButtonColor.WDK24PrimaryLightBg]: ButtonColor.WDK24PrimaryLightBg,
  [ButtonColor.WDK24SecondaryLightBg]: ButtonColor.WDK24SecondaryLightBg,
  [ButtonColor.WDK24TertiaryLightBg]: ButtonColor.WDK24TertiaryLightBg,
  [ButtonColor.WDK24PrimaryDarkBg]: ButtonColor.WDK24PrimaryDarkBg,
  [ButtonColor.WDK24SecondaryDarkBg]: ButtonColor.WDK24SecondaryDarkBg,
  [ButtonColor.WDK24TertiaryDarkBg]: ButtonColor.WDK24TertiaryDarkBg,
  [ButtonColor.WDK24LimeGreenDarkBg]: ButtonColor.WDK24LimeGreenDarkBg,
  [ButtonColor.WDK24LimeGreenBlackTextDarkBg]: ButtonColor.WDK24LimeGreenBlackTextDarkBg,
  [ButtonColor.WDK24GoogleSecondaryDarkBg]: ButtonColor.WDK24GoogleSecondaryDarkBg,
  [ButtonColor.WDK24BlackWhiteText]: ButtonColor.WDK24BlackWhiteText,
  [ButtonColor.WDK24WhiteBlackText]: ButtonColor.WDK24WhiteBlackText,

  [ButtonColor.PrimaryGreen]: ButtonColor.WDK24PrimaryLightBg,
  [ButtonColor.PrimaryOnDarkBg]: ButtonColor.WDK24PrimaryDarkBg,
  [ButtonColor.Secondary]: ButtonColor.WDK24SecondaryLightBg,
  [ButtonColor.SecondaryOnDarkBg]: ButtonColor.WDK24SecondaryDarkBg,
  [ButtonColor.Tertiary]: ButtonColor.WDK24TertiaryLightBg,
  [ButtonColor.GoPrimaryOnDarkBg]: ButtonColor.WDK24PrimaryDarkBg,
  [ButtonColor.GoSecondaryOnDarkBg]: ButtonColor.WDK24SecondaryDarkBg,

  // The following buttons will be deprecated
  [ButtonColor.Transparent]: ButtonColor.Transparent,
  [ButtonColor.PrimaryBlue]: ButtonColor.PrimaryBlue,
  [ButtonColor.SecondaryGreen]: ButtonColor.SecondaryGreen,
  [ButtonColor.SecondaryBlue]: ButtonColor.SecondaryBlue,
}

export const buttonColorToClass = (color?: ButtonColor, convertTo2024 = false): string | undefined => {
  if (color) {
    const finalColor = convertTo2024 ? buttonColorTo2024[color] : color
    return buttonColorMap[finalColor]
  }
  return undefined
}

export interface ButtonCtaProps extends Omit<BadgerButtonProps, 'status'> {
  color?: ButtonColor
  compact?: boolean
  status?: ButtonStatus
  text: React.ReactNode
  showArrow?: boolean
}
